import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, TextInput, Alert } from '../../components';
import { LogoMark } from '../../components/branding/Logo';
import { API_URL } from '../../api/client';

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check sessionStorage for return URL (from /create flow), then location state, then default
  const returnUrl = sessionStorage.getItem('auth-return-url');
  const from = returnUrl || (location.state as { from?: { pathname: string } })?.from?.pathname || '/app/home';

  // Check for OAuth error in URL
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError('Google sign-in failed. Please try again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
      // Clear the return URL from sessionStorage after using it
      sessionStorage.removeItem('auth-return-url');
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string; details?: Record<string, string[]> } } } };

      // Extract validation errors from Laravel response
      if (error.response?.data?.error?.details) {
        const validationErrors = Object.values(error.response.data.error.details).flat();
        setError(validationErrors.join('\n'));
      } else {
        setError(error.response?.data?.error?.message || 'Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Store the intended destination for after OAuth
    sessionStorage.setItem('oauth_redirect', from);
    window.location.href = `${API_URL}/api/v1/auth/google`;
  };

  return (
    <div className="max-w-sm mx-auto">
      <div className="bg-background-card rounded-xl p-6 md:p-8 shadow-md">
        <div className="text-center mb-6 md:mb-8">
          <LogoMark className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 md:hidden" />
          <h2 className="text-xl md:text-2xl font-semibold text-text-primary mb-1 md:mb-2">Welcome back</h2>
          <p className="text-sm md:text-base text-text-secondary">Sign in to your account</p>
        </div>

        {/* Google Sign In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="btn btn-white w-full"
        >
          <GoogleIcon />
          Continue with Google
        </button>
        <p className="text-xs text-text-secondary text-center mt-2">
          By continuing you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>

        {/* Divider */}
        <div className="flex items-center gap-4 my-4 md:my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs md:text-sm text-text-secondary">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
          <TextInput
            type="email"
            label="Email"
            placeholder="you@example.com"
            icon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-text-primary">Password</label>
              <div className="relative">
                <TextInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  icon={Lock}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 inset-y-0 flex items-center text-text-secondary hover:text-text-primary"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="mt-2 text-center">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          {error && (
            <Alert variant="error">
              <span className="whitespace-pre-line">{error}</span>
            </Alert>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </div>
        </form>
      </div>

      <p className="text-center mt-6 text-text-secondary">
        Don't have an account?{' '}
        <Link to="/register" className="text-primary font-medium">
          Sign up
        </Link>
      </p>
    </div>
  );
}
